/**
 * VersionCheckOrchestrator
 * Main orchestrator that coordinates all version check agents and validators
 * 
 * Responsibilities:
 * - Initialize all agents and validators
 * - Coordinate polling lifecycle
 * - Handle Page Visibility API (pause when tab hidden)
 * - Resume checking when tab becomes visible
 * - Manage state flow between agents
 */

import VersionAPIAgent from './agents/VersionAPIAgent';
import VersionCheckAgent from './agents/VersionCheckAgent';
import UpdateBannerAgent from './agents/UpdateBannerAgent';
import VersionValidator from './validators/VersionValidator';
import UIValidator from './validators/UIValidator';

class VersionCheckOrchestrator {
  constructor(options = {}) {
    // Configuration
    this.options = {
      pollInterval: options.pollInterval || 300000, // 5 minutes default
      mobilePollInterval: options.mobilePollInterval || 600000, // 10 minutes on mobile
      immediateCheck: options.immediateCheck !== false, // Check immediately on init
      enableVisibilityHandling: options.enableVisibilityHandling !== false,
      onUpdateAvailable: options.onUpdateAvailable || null,
      onError: options.onError || console.error,
      debug: options.debug || false
    };

    // Initialize agents (these are objects, not classes)
    this.versionAPIAgent = VersionAPIAgent;
    this.versionCheckAgent = VersionCheckAgent;
    this.updateBannerAgent = UpdateBannerAgent;

    // Initialize validators (these are classes)
    this.versionValidator = new VersionValidator();
    this.uiValidator = new UIValidator();

    // State
    this.initialized = false;
    this.isPolling = false;
    this.isTabVisible = true;
    this.lastCheckTime = null;
    this.checksSinceInit = 0;
    this.errors = [];

    // Bind methods
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    this._performVersionCheck = this._performVersionCheck.bind(this);
  }

  /**
   * Initialize the orchestrator and all agents
   * @returns {Promise<{ success: boolean, initialVersion?: string, error?: string }>}
   */
  async initialize() {
    if (this.initialized) {
      this._log('Orchestrator already initialized');
      return { success: true };
    }

    this._log('Initializing VersionCheckOrchestrator...');

    try {
      // Validate storage availability
      const storageValidation = this.versionValidator.VALIDATE_STORAGE();
      if (!storageValidation.valid) {
        throw new Error(`Storage validation failed: ${storageValidation.error}`);
      }

      // Validate poll interval
      const isMobile = this._detectMobile();
      const pollInterval = isMobile ? this.options.mobilePollInterval : this.options.pollInterval;
      const intervalValidation = this.versionValidator.VALIDATE_POLL_INTERVAL(pollInterval);
      
      if (!intervalValidation.valid && intervalValidation.adjusted) {
        this._log(`Adjusting poll interval from ${pollInterval} to ${intervalValidation.adjusted}`);
        if (isMobile) {
          this.options.mobilePollInterval = intervalValidation.adjusted;
        } else {
          this.options.pollInterval = intervalValidation.adjusted;
        }
      }

      // Set up visibility change handling
      if (this.options.enableVisibilityHandling && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this._handleVisibilityChange);
        this._log('Visibility change listener attached');
      }

      // Subscribe to banner state changes
      this.updateBannerAgent.skills.SUBSCRIBE((state) => {
        this._log('Banner state changed:', state);
        if (this.options.onUpdateAvailable && state.visible) {
          this.options.onUpdateAvailable(state);
        }
      });

      // Perform immediate check if enabled
      if (this.options.immediateCheck) {
        const checkResult = await this._performVersionCheck();
        this._log('Initial version check result:', checkResult);
      }

      // Start polling
      this._startPolling();

      this.initialized = true;
      this._log('VersionCheckOrchestrator initialized successfully');

      return {
        success: true,
        initialVersion: this.versionCheckAgent.skills.GET_STORED_VERSION()
      };
    } catch (error) {
      this.errors.push({ time: Date.now(), error: error.message });
      this.options.onError(error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Perform a version check
   * @private
   */
  async _performVersionCheck() {
    this._log('Performing version check...');
    this.lastCheckTime = Date.now();
    this.checksSinceInit++;

    try {
      // Fetch version via API agent
      const apiResult = await this.versionAPIAgent.skills.GET_VERSION();

      if (!apiResult.success) {
        throw new Error(apiResult.error || 'Failed to fetch version');
      }

      // Validate API response
      const responseValidation = this.versionValidator.VALIDATE_API_RESPONSE(apiResult.data);
      if (!responseValidation.valid) {
        throw new Error(`Invalid API response: ${responseValidation.error}`);
      }

      // Extract version
      const newVersion = this.versionAPIAgent.skills.RETURN_SHA(apiResult.data);

      // Detect if there's a change
      const changeResult = this.versionCheckAgent.skills.DETECT_CHANGE(newVersion);

      if (changeResult.updateAvailable) {
        this._log('Update available:', changeResult.newVersion);
        
        // Show update banner
        this.updateBannerAgent.skills.SHOW_BANNER({
          message: 'A new version is available. Tap to refresh.',
          type: 'info',
          newVersion: changeResult.newVersion
        });

        // Validate banner state
        const bannerState = this.updateBannerAgent.skills.GET_STATE();
        const bannerValidation = this.uiValidator.VALIDATE_BANNER_STATE(bannerState);
        
        if (!bannerValidation.valid) {
          this._log('Banner state validation warnings:', bannerValidation.errors);
        }
      }

      return {
        success: true,
        version: newVersion,
        updateAvailable: changeResult.updateAvailable
      };
    } catch (error) {
      this.errors.push({ time: Date.now(), error: error.message });
      this._log('Version check error:', error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Start polling for version updates
   * @private
   */
  _startPolling() {
    if (this.isPolling) {
      this._log('Already polling, skipping start');
      return;
    }

    const isMobile = this._detectMobile();
    const interval = isMobile ? this.options.mobilePollInterval : this.options.pollInterval;

    this._log(`Starting polling with ${interval}ms interval (mobile: ${isMobile})`);

    this.versionCheckAgent.skills.POLL_VERSION({
      interval,
      immediate: false, // We already did immediate check in initialize
      checkFn: this._performVersionCheck
    });

    this.isPolling = true;
  }

  /**
   * Stop polling for version updates
   * @private
   */
  _stopPolling() {
    if (!this.isPolling) {
      return;
    }

    this._log('Stopping polling');
    this.versionCheckAgent.skills.STOP_POLLING();
    this.isPolling = false;
  }

  /**
   * Handle visibility change events
   * @private
   */
  _handleVisibilityChange() {
    const isVisible = document.visibilityState === 'visible';
    
    if (isVisible === this.isTabVisible) {
      return; // No change
    }

    this.isTabVisible = isVisible;
    this._log(`Tab visibility changed: ${isVisible ? 'visible' : 'hidden'}`);

    if (isVisible) {
      // Tab became visible - check immediately and resume polling
      this._performVersionCheck();
      this._startPolling();
    } else {
      // Tab hidden - pause polling to save resources
      this._stopPolling();
    }
  }

  /**
   * Manually trigger a version check
   * @returns {Promise<{ success: boolean, updateAvailable: boolean }>}
   */
  async checkNow() {
    return this._performVersionCheck();
  }

  /**
   * Dismiss the update banner
   */
  dismissBanner() {
    this.updateBannerAgent.skills.HIDE_BANNER();
  }

  /**
   * Trigger a page reload to get the new version
   * @param {boolean} hard - If true, performs a hard reload (clears cache)
   */
  triggerReload(hard = true) {
    this.updateBannerAgent.skills.TRIGGER_RELOAD({ hard });
  }

  /**
   * Get the current state of the orchestrator
   * @returns {object}
   */
  getState() {
    return {
      initialized: this.initialized,
      isPolling: this.isPolling,
      isTabVisible: this.isTabVisible,
      lastCheckTime: this.lastCheckTime,
      checksSinceInit: this.checksSinceInit,
      currentVersion: this.versionCheckAgent.skills.GET_STORED_VERSION(),
      bannerState: this.updateBannerAgent.skills.GET_STATE(),
      isMobile: this._detectMobile(),
      errors: this.errors.slice(-10) // Last 10 errors
    };
  }

  /**
   * Subscribe to banner state changes
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  subscribe(callback) {
    return this.updateBannerAgent.skills.SUBSCRIBE(callback);
  }

  /**
   * Clean up and destroy the orchestrator
   */
  destroy() {
    this._log('Destroying VersionCheckOrchestrator...');

    // Stop polling
    this._stopPolling();

    // Remove visibility listener
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    }

    // Reset state
    this.initialized = false;
    this.errors = [];

    this._log('VersionCheckOrchestrator destroyed');
  }

  /**
   * Run all validations and return health status
   * @returns {object}
   */
  runHealthCheck() {
    const versionValidations = this.versionValidator.RUN_ALL_VALIDATIONS({
      pollInterval: this._detectMobile() ? this.options.mobilePollInterval : this.options.pollInterval,
      version: this.versionCheckAgent.skills.GET_STORED_VERSION()
    });

    const uiValidations = this.uiValidator.RUN_ALL_VALIDATIONS({
      state: this.updateBannerAgent.skills.GET_STATE(),
      mobileConfig: {
        checkViewport: true
      },
      a11yConfig: {
        ariaLive: 'polite',
        role: 'alert'
      }
    });

    return {
      healthy: versionValidations.allValid && uiValidations.allValid,
      version: versionValidations,
      ui: uiValidations,
      orchestrator: this.getState()
    };
  }

  /**
   * Detect if running on mobile device
   * @private
   */
  _detectMobile() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Log debug messages
   * @private
   */
  _log(...args) {
    if (this.options.debug) {
      console.log('[VersionCheckOrchestrator]', ...args);
    }
  }
}

export default VersionCheckOrchestrator;
